import moment from 'moment'
import authStorage from 'providers/authStorage.js';

let yup = require('yup');

const capitalizeName = (v) => {
  if (!v || typeof v !== 'string') return v;
  return v.trim().split(/\s+/).map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
};

const helpers = {
  capitalizeName,
  getAllValues: (objects = [], key) => {
    return objects.length > 0 ? objects.map(item => {
      return item[key]
    }) : []
  },
  getYupRequired: (message) => yup.string()
    .required(`${message} is required`)
    .nullable(`${message} is required`)
  ,
  getYupCapitalizedRequired: (message) => yup.string()
    .required(`${message} is required`)
    .transform(v => helpers.capitalizeName(v))
    .strict(false)
    .test('no-numbers-symbols', `${message} can only contain letters, spaces, hyphens and apostrophes`, (val) => {
      if (!val) return true;
      return /^[A-Za-z\s\-']+$/.test(val.trim());
    })
    .nullable(`${message} is required`)
  ,
  getYupMiddleNameOptional: (message) => yup.string()
    .nullable()
    .transform(v => helpers.capitalizeName(v))
    .strict(false)
    .test('no-numbers-symbols', `${message} can only contain letters, spaces, hyphens and apostrophes`, (val) => {
      if (!val) return true;
      return /^[A-Za-z\s\-']+$/.test(val.trim());
    })
  ,
  getYupImageOptional: (message, maxMB = 2, allowedMimes = ['image/png', 'image/jpeg']) => yup
    .mixed()
    .nullable()
    .test('file-type', `${message} must be an image (${allowedMimes.join(', ')})`, (val) => {
      if (!val) return true;
      // If it's a string (existing filename), skip type check
      if (typeof val === 'string') return true;
      // If it's a File or Blob, validate mime
      const mime = val && val.type ? val.type : '';
      return allowedMimes.includes(mime);
    })
    .test('file-size', `${message} must be <= ${maxMB}MB`, (val) => {
      if (!val) return true;
      if (typeof val === 'string') return true;
      const size = val && val.size ? val.size : 0;
      return size <= maxMB * 1024 * 1024;
    })
  ,
  getYupImageRequired: (message, maxMB = 2, allowedMimes = ['image/png', 'image/jpeg']) => yup
    .mixed()
    .required(`${message} is required`)
    .test('file-type', `${message} must be an image (${allowedMimes.join(', ')})`, (val) => {
      if (!val) return false;
      if (typeof val === 'string') return true;
      const mime = val && val.type ? val.type : '';
      return allowedMimes.includes(mime);
    })
    .test('file-size', `${message} must be <= ${maxMB}MB`, (val) => {
      if (!val) return false;
      if (typeof val === 'string') return true;
      const size = val && val.size ? val.size : 0;
      return size <= maxMB * 1024 * 1024;
    })
    .nullable(`${message} is required`)
  ,
  getYupMixedRequired: (message) => yup.mixed()
    .required(`${message} is required`)
    .nullable(`${message} is required`)
  ,
  getYupArrayRequired: (message) => yup.array()
    .min(1, `${message} is required`)
    .required(`${message} is required`)
  ,
  getYupPhoneRequired: (message, min = 6, max = 13) => yup
    .string()
    .required(`${message} is required`)
    .test('phone-length', `Phone must be between ${min}-${max} digits`, (val) => {
      try {
        const digits = ('' + val).replace(/\D/g, '');
        return digits.length >= min && digits.length <= max;
      } catch {
        return false;
      }
    })
  ,
  getYupPhoneOptional: (message, min = 6, max = 13) => yup
    .string()
    .nullable()
    .test('phone-length-optional', `Phone must be between ${min}-${max} digits`, (val) => {
      if (val === undefined || val === null || val === '') return true;
      try {
        const digits = ('' + val).replace(/\D/g, '');
        return digits.length >= min && digits.length <= max;
      } catch {
        return false;
      }
    })
  ,
  getYupZipRequired: (message) => yup
    .string()
    .required(`${message} is required`)
    .matches(/^[a-zA-Z0-9\s-]+$/, 'Zip code can only contain letters, numbers, spaces, and hyphens')
    .min(3, 'Zip code must be at least 3 characters')
    .max(10, 'Zip code must not exceed 10 characters')
    .nullable(`${message} is required`)
  ,
  getYupZipOptional: (message) => yup
    .string()
    .nullable()
    .test('zip-format-optional', 'Zip code can only contain letters, numbers, spaces, and hyphens', (val) => {
      if (!val || val === '') return true;
      return /^[a-zA-Z0-9\s-]+$/.test(val);
    })
    .test('zip-min-optional', 'Zip code must be at least 3 characters', (val) => {
      if (!val || val === '') return true;
      return val.length >= 3;
    })
    .test('zip-max-optional', 'Zip code must not exceed 10 characters', (val) => {
      if (!val || val === '') return true;
      return val.length <= 10;
    })
  ,
  getYupNameRequired: (message) => yup
    .string()
    .required(`${message} is required`)
    .nullable(`${message} is required`)
  ,
  getYupNameOptional: (message) => yup
    .string()
    .nullable()
  ,
  getYupOptional: (message) => yup
    .string()
    .nullable()
  ,
  getYupAlphaSpaceHyphenRequired: (message) => yup
    .string()
    .required(`${message} is required`)
    .matches(/^[A-Za-z]+(?:[ -][A-Za-z]+)*$/, `${message} can only contain letters, spaces, and hyphens`)
    .min(2, `${message} must be at least 2 characters`)
    .max(100, `${message} must not exceed 100 characters`)
    .nullable(`${message} is required`)
  ,
  getYupAddress1Required: (message) => yup
    .string()
    .required(`${message} is required`)
    .nullable(`${message} is required`)
  ,
  getYupCityRequired: (message) => yup
    .string()
    .required(`${message} is required`)
    .matches(/^[a-zA-Z0-9\s._,-]+$/, `${message} can only contain letters, numbers, spaces, underscores, hyphens, commas, and periods`)
    .min(2, `${message} must be at least 2 characters`)
    .max(100, `${message} must not exceed 100 characters`)
    .nullable(`${message} is required`)
  ,
  getYupCityOptional: (message) => yup
    .string()
    .nullable()
    .test('city-format-optional', `${message} can only contain letters, numbers, spaces, underscores, hyphens, commas, and periods`, (val) => {
      if (!val || val === '') return true;
      return /^[a-zA-Z0-9\s._,-]+$/.test(val);
    })
    .test('city-min-optional', `${message} must be at least 2 characters`, (val) => {
      if (!val || val === '') return true;
      return val.length >= 2;
    })
    .test('city-max-optional', `${message} must not exceed 100 characters`, (val) => {
      if (!val || val === '') return true;
      return val.length <= 100;
    })
  ,
  getYupDateDayRequired: (message, shortMessage = false) => yup
    .number()
    .typeError(shortMessage ? 'Date' : 'Date must be a number')
    .min(1, shortMessage ? 'Date' : 'Invalid date')
    .max(31, shortMessage ? 'Date' : 'Invalid date')
    .required(shortMessage ? 'Date' : `${message} is required`)
    .nullable(shortMessage ? 'Date' : `${message} is required`)
  ,
  getYupDateDayOptional: (message) => yup
    .number()
    .typeError('Date must be a number')
    .nullable()
    .transform((v, o) => o === '' ? null : v)
    .test('range', 'Invalid date', (val) => val === null || (val >= 1 && val <= 31))
  ,
  getYupDateMonthRequired: (message, shortMessage = false) => yup
    .number()
    .typeError(shortMessage ? 'Month' : 'Month must be a number')
    .min(1, shortMessage ? 'Month' : 'Invalid month')
    .max(12, shortMessage ? 'Month' : 'Invalid month')
    .required(shortMessage ? 'Month' : `${message} is required`)
    .nullable(shortMessage ? 'Month' : `${message} is required`)
  ,
  getYupDateMonthOptional: (message) => yup
    .number()
    .typeError('Month must be a number')
    .nullable()
    .transform((v, o) => o === '' ? null : v)
    .test('range', 'Invalid month', (val) => val === null || (val >= 1 && val <= 12))
  ,
  getYupDateYearRequired: (message, shortMessage = false) => yup
    .string()
    .matches(/^\d+$/)
    .required(shortMessage ? 'Year' : 'Year is required')
    .min(4, shortMessage ? 'Year' : 'Invalid year')
    .max(4, shortMessage ? 'Year' : 'Invalid year')
  ,
  getYupDateYearOptional: (message) => yup
    .string()
    .nullable()
    .transform((v, o) => o === '' ? null : v)
    .test('length', 'Invalid year', (val) => val === null || /^\d{4}$/.test(val))
    .test('no-future', 'Future year not allowed', (val) => {
      if (!val) return true;
      return parseInt(val) <= new Date().getFullYear();
    })
  ,
  getYupTimeHoursOptional: (message) => yup
    .number()
    .typeError('Hours must be a number')
    .nullable()
    .transform((v, o) => o === '' ? null : v)
    .test('range', 'Invalid hours', (val) => val === null || (val >= 0 && val <= 12))
  ,
  getYupTimeMinutesOptional: (message) => yup
    .number()
    .typeError('Minutes must be a number')
    .nullable()
    .transform((v, o) => o === '' ? null : v)
    .test('range', 'Invalid minutes', (val) => val === null || (val >= 0 && val <= 59))
  ,
  getYupWorkPhoneRequired: (message, shortMessage = false) => yup
    .string()
    .required(shortMessage ? 'Work Phone' : 'Work Phone is required')
    .matches(/^[0-9]{10}$/, shortMessage ? 'Work Phone' : 'Invalid work phone')
  ,

  getYupTimeHoursRequired: (message, shortMessage = false) => yup
    .number()
    .typeError(shortMessage ? 'Hours' : 'Hours must be a number')
    .min(0, shortMessage ? 'Hours' : 'Invalid hours')
    .max(12, shortMessage ? 'Hours' : 'Invalid hours')
    .required(shortMessage ? 'Hours' : `${message} is required`)
  ,
  getYupTimeMinutesRequired: (message, shortMessage = false) => yup
    .number()
    .typeError(shortMessage ? 'Minutes' : 'Minutes must be a number')
    .min(0, shortMessage ? 'Minutes' : 'Invalid minutes')
    .max(59, shortMessage ? 'Minutes' : 'Invalid minutes')
    .required(shortMessage ? 'Minutes' : `${message} is required`)
  ,
  getYupEmailOptional: (message) => yup
    .string()
    .trim()
    .nullable()
    .test('email-validation', 'Invalid email format', (val) => {
      if (val === undefined || val === null || val === '') return true;
      const v = String(val).trim();

      // Email regex pattern that enforces:
      // - Must start with a letter (a-z, A-Z)
      // - Can contain letters, numbers, dots, hyphens, underscores
      // - Must have @ symbol
      // - Must have domain with at least one dot
      // - No spaces allowed
      // - No special characters like #,$,*,&,(,),=,<,>,/,?,|,~
      const emailRegex = /^[A-Za-z](?:[A-Za-z0-9._+-]*[A-Za-z0-9])?@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)+$/;

      if (!emailRegex.test(v)) return false;

      // Additional check: no consecutive dots
      if (/\.\./.test(val)) return false;

      // Additional check: no spaces
      if (/\s/.test(v)) return false;

      return true;
    })
  ,
  getYupEmailRequired: (message) => yup
    .string()
    .trim()
    .required(`${message} is required`)
    .test('email-validation', 'Invalid email format', (val) => {
      if (val === undefined || val === null || val === '') return false;
      const v = String(val).trim();

      // Email regex pattern that enforces:
      // - Must start with a letter (a-z, A-Z)
      // - Can contain letters, numbers, dots, hyphens, underscores, plus signs
      // - Must have @ symbol
      // - Must have domain with at least one dot
      // - No spaces allowed
      // - No special characters like #,$,*,&,(,),=,<,>,/,?,|,~
      const emailRegex = /^[A-Za-z](?:[A-Za-z0-9._+-]*[A-Za-z0-9])?@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)+$/;

      if (!emailRegex.test(v)) return false;

      // Additional check: no consecutive dots
      if (/\.\./.test(v)) return false;

      // Additional check: no spaces
      if (/\s/.test(v)) return false;

      return true;
    })
    .nullable(`${message} is required`)
  ,
  stringToArr: (str) => {
    return (typeof str === 'string' ? str.split(', ') : []).filter((item) => item !== '');
  },

  idConverter: (array, searchInArray, searchKey, returnKey) => {
    return array.map(searchItem => searchInArray.find(object => object[searchKey] === searchItem)[returnKey])
  },

  byHashByIDConfToArray: (byID, byHash) => {
    return byID.map(id => {
      return { ...byHash[id], id: id }
    })
  },
  getFormattedDateTime: (serverDate) => {
    if (serverDate === null) return false;
    return moment(serverDate).format("MM/DD/YYYY HH:mm")
  },
  getFormattedDate: (serverDate) => {
    if (serverDate === null) return false;
    return moment(serverDate).format("MM/DD/YYYY")
  },
  convertAMPMTimeTo24Format: (hours, minutes, AMPM) => {
    let nHours = Number(hours);
    let nMinutes = Number(minutes);
    if (AMPM === "pm" && nHours < 12) nHours = nHours + 12;
    if (AMPM === "am" && nHours === 12) nHours = nHours - 12;
    let sHours = nHours.toString();
    let sMinutes = nMinutes.toString();
    if (nHours < 10) sHours = "0" + sHours;
    if (minutes < 10) sMinutes = "0" + sMinutes;
    return sHours + ":" + sMinutes;
  },
  can: (roles) => {
    const access = authStorage.getAccess();
    if (access && access.hasOwnProperty('userRoleCode')) {
      return roles.includes(access.userRoleCode);
    } else return false
  },

  isFunction: (functionToCheck) => {
    return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
  }
}

const can = (roles) => {
  return helpers.can(roles);
};

const cannot = (roles) => {
  const access = authStorage.getAccess();
  if (access && access.hasOwnProperty('userRoleCode')) {
    return !roles.includes(access.userRoleCode);
  } else return false
};

const leaf = (obj, path) => (path.split('.').reduce((value, el) => value[el], obj))

const getShadeColor = (colorHex, percent, transparency = 1) => {
  let R = parseInt(colorHex.substring(1, 3), 16);
  let G = parseInt(colorHex.substring(3, 5), 16);
  let B = parseInt(colorHex.substring(5, 7), 16);

  R = parseInt(R * (100 + percent) / 100);
  G = parseInt(G * (100 + percent) / 100);
  B = parseInt(B * (100 + percent) / 100);

  R = (R < 255) ? R : 255;
  G = (G < 255) ? G : 255;
  B = (B < 255) ? B : 255;

  if (transparency < 1) {
    return `rgba(${R}, ${G}, ${B}, ${transparency})`;
  }

  let RR = ((R.toString(16).length === 1) ? "0" + R.toString(16) : R.toString(16));
  let GG = ((G.toString(16).length === 1) ? "0" + G.toString(16) : G.toString(16));
  let BB = ((B.toString(16).length === 1) ? "0" + B.toString(16) : B.toString(16));

  return "#" + RR + GG + BB;
}

const concatWithCommaSeparation = (array) => {
  let value = '';

  array.forEach((item, index) => {
    const renderValue = index > 0 ? `, ${item}` : item;
    if (item) {
      value += renderValue;
    }
  })

  return value;
}

const filterArrayOfObjectsByArray = (array = [], needleArray = [], needleKey = 'id') => {
  return array.filter(function (item) {
    return needleArray.includes(item[needleKey]);
  })

}

export { leaf, getShadeColor, can, concatWithCommaSeparation, filterArrayOfObjectsByArray, cannot };

export default helpers